import PageLayout from "@/components/feature/PageLayout";
import ScrollReveal from "@/components/base/ScrollReveal";
import { BLOG_HERO_IMAGE } from "@/data/blogAssets";
import { blogPosts } from "@/mocks/blogPosts";

const categories = [
  "All",
  "Getting Started",
  "Anxiety",
  "Relationships",
  "Teen Therapy",
  "Mental Health",
];

const localBlogPosts = blogPosts.map((p) => ({
  title: p.title,
  excerpt: p.excerpt,
  category: p.category,
  date: p.date,
  slug: p.slug,
  color: p.color,
  bar: p.bar,
  readTime: p.readTime,
}));

export default function Blog() {
  return (
    <>
      <title>Blog | Perspective Therapy Mental Health Insights Larchmont NY</title>
      <meta name="description" content="Read articles on mental health, therapy, anxiety, relationships, and teen wellness from Perspective Therapy in Larchmont, NY." />
      <PageLayout>
        {/* Hero */}
        <section className="relative w-full min-h-[420px] md:min-h-[500px] flex items-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src={BLOG_HERO_IMAGE}
              alt="Perspective Therapy blog"
              className="w-full h-full object-cover object-top"
            />
            <div
              className="absolute inset-0"
              style={{
                background: "linear-gradient(120deg, rgba(28,20,12,0.48) 0%, rgba(42,30,18,0.32) 50%, rgba(60,40,20,0.14) 100%)",
              }}
            />
            <div
              className="absolute inset-0"
              style={{
                background: "linear-gradient(to top, rgba(20,14,8,0.38) 0%, transparent 50%)",
              }}
            />
          </div>
          <div className="relative z-10 w-full px-6 md:px-12 lg:px-20 pt-36 pb-20">
            <div className="max-w-2xl">
              <p className="font-manrope text-white/90 text-[13px] font-semibold tracking-widest uppercase mb-4">
                Insights & Resources
              </p>
              <h1 className="font-fraunces text-white text-4xl md:text-5xl lg:text-[3.4rem] font-light leading-[1.15] mb-6">
                The Perspective{" "}
                <em className="not-italic font-normal" style={{ color: "#C9BCEE" }}>
                  blog
                </em>
              </h1>
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-3 md:p-4 max-w-lg">
                <p className="font-manrope text-[#2B2B2B]/90 text-lg leading-relaxed">
                  Thoughtful articles on mental health, therapy, relationships, and personal growth. Written by the team at Perspective Therapy.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Blog Grid */}
        <section className="w-full py-20 md:py-28" style={{ background: "linear-gradient(180deg, #F8F5EE 0%, #F5F1E8 45%, #F2EDE4 100%)" }}>
          <div className="w-full px-6 md:px-12 lg:px-20">
            <div className="max-w-3xl mb-12 md:mb-14">
              <ScrollReveal variant="fadeIn" delay={0}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-5 h-[2px] rounded-full" style={{ background: "linear-gradient(90deg, #6B742F, #B4A6E3)" }} />
                  <span className="font-manrope text-[13px] font-semibold tracking-widest uppercase" style={{ color: "#6B742F" }}>
                    Latest Articles
                  </span>
                </div>
              </ScrollReveal>
              <ScrollReveal variant="fadeUp" delay={80}>
                <h2 className="font-fraunces text-[#2B2B2B] text-3xl md:text-4xl font-light leading-snug mb-5">
                  Explore articles on{" "}
                  <em className="not-italic" style={{ color: "#7D54FC" }}>
                    mental health and growth
                  </em>
                </h2>
              </ScrollReveal>
              <ScrollReveal variant="fadeUp" delay={160}>
                <p className="font-manrope text-[#2B2B2B]/78 text-[15px] leading-relaxed max-w-lg">
                  Our blog covers practical insights on therapy, anxiety, relationships, teen mental health, and navigating life with more clarity.
                </p>
              </ScrollReveal>
            </div>

            {/* Category pills */}
            <ScrollReveal variant="fadeUp" delay={200}>
              <div className="flex flex-wrap gap-2 mb-10">
                {categories.map((cat) => (
                  <span
                    key={cat}
                    className="font-manrope text-[13px] font-semibold px-4 py-2 rounded-full cursor-pointer transition-all whitespace-nowrap"
                    style={{
                      background: cat === "All" ? "#3C6FF8" : "rgba(43,43,43,0.06)",
                      color: cat === "All" ? "#fff" : "rgba(43,43,43,0.65)",
                    }}
                  >
                    {cat}
                  </span>
                ))}
              </div>
            </ScrollReveal>

            {/* Posts grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-7">
              {localBlogPosts.map((post, i) => (
                <ScrollReveal key={post.slug} variant="fadeUp" delay={i * 90} threshold={0.06}>
                  <a
                    href={`/blog/${post.slug}`}
                    className="bg-white/80 border rounded-2xl overflow-hidden flex flex-col h-full card-hover block"
                    style={{ borderColor: post.color + "22" }}
                  >
                    <div className="h-[3px] w-full shrink-0" style={{ background: post.bar }} />
                    <div className="p-6 md:p-7 flex flex-col flex-1">
                      <div className="flex items-center gap-2 mb-4">
                        <span
                          className="font-manrope text-[11px] font-semibold px-2.5 py-1 rounded-full"
                          style={{
                            background: post.color + "12",
                            color: post.color,
                          }}
                        >
                          {post.category}
                        </span>
                        <span className="font-manrope text-[12px] text-[#2B2B2B]/45">
                          {post.date} &middot; {post.readTime}
                        </span>
                      </div>
                      <h3 className="font-fraunces text-[#2B2B2B] text-lg font-medium mb-3 leading-snug flex-1">
                        {post.title}
                      </h3>
                      <p className="font-manrope text-[#2B2B2B]/75 text-[14px] leading-relaxed mb-5">
                        {post.excerpt}
                      </p>
                      <span
                        className="inline-flex items-center gap-1.5 font-manrope text-[13px] font-semibold mt-auto"
                        style={{ color: post.color }}
                      >
                        Read article <i className="ri-arrow-right-line text-xs"></i>
                      </span>
                    </div>
                  </a>
                </ScrollReveal>
              ))}
            </div>

            {/* View all CTA */}
            <ScrollReveal variant="fadeUp" delay={400}>
              <div className="flex justify-center mt-12">
                <a
                  href="/blog/all"
                  className="inline-flex items-center gap-2 font-manrope font-semibold text-[15px] px-7 py-4 rounded-full transition-colors cursor-pointer whitespace-nowrap border"
                  style={{
                    color: "#2B2B2B",
                    borderColor: "rgba(43,43,43,0.18)",
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLAnchorElement).style.background = "#2B2B2B";
                    (e.currentTarget as HTMLAnchorElement).style.color = "#fff";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLAnchorElement).style.background = "transparent";
                    (e.currentTarget as HTMLAnchorElement).style.color = "#2B2B2B";
                  }}
                >
                  View All Articles <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* Subscribe */}
        <section className="w-full py-20 md:py-28" style={{ background: "linear-gradient(180deg, #EEE8DA 0%, #F5F1E8 45%, #F8F5EE 100%)" }}>
          <div className="w-full px-6 md:px-12 lg:px-20">
            <div className="max-w-xl mx-auto text-center">
              <ScrollReveal variant="fadeUp" delay={0}>
                <div className="flex items-center justify-center gap-2 mb-4">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full" style={{ background: "rgba(60,111,248,0.09)" }}>
                    <i className="ri-mail-line text-sm" style={{ color: "#3C6FF8" }}></i>
                  </div>
                </div>
                <h2 className="font-fraunces text-[#2B2B2B] text-3xl md:text-4xl font-light leading-snug mb-4">
                  Want more insights delivered to your inbox?
                </h2>
              </ScrollReveal>
              <ScrollReveal variant="fadeUp" delay={80}>
                <p className="font-manrope text-[#2B2B2B]/78 text-[15px] leading-relaxed mb-8 max-w-md mx-auto">
                  Get blog notifications with practical mental health tips, new articles, and resources from Perspective Therapy.
                </p>
              </ScrollReveal>
              <ScrollReveal variant="fadeUp" delay={160}>
                <a
                  href="/blog/all"
                  className="inline-flex items-center justify-center gap-2 text-white font-manrope font-semibold text-[15px] px-7 py-4 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                  style={{ background: "#3C6FF8" }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLAnchorElement).style.background = "#2B5BE0";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLAnchorElement).style.background = "#3C6FF8";
                  }}
                >
                  Subscribe for Blog Updates <i className="ri-mail-send-line"></i>
                </a>
              </ScrollReveal>
            </div>
          </div>
        </section>
      </PageLayout>
    </>
  );
}
